import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { BookOpen, Brain, TrendingUp } from 'lucide-react';

// Apple-inspired easing
const appleEase: [number, number, number, number] = [0.22, 1, 0.36, 1];
const duration = 0.8;
const stagger = 0.1;

const steps = [
  {
    icon: BookOpen,
    number: '01',
    title: 'Choose your course',
    description: 'Select from TASC-aligned subjects for Year 10, 11 and 12. We build a personalised study plan around your goals.',
  },
  {
    icon: Brain,
    number: '02',
    title: 'Learn with Dev',
    description: 'Your AI tutor adapts to your pace, providing hints and guidance exactly when you need them.',
  },
  {
    icon: TrendingUp,
    number: '03',
    title: 'Track your growth',
    description: 'See your improvement over time. Weekly reports show what you\'ve mastered and what needs attention.',
  },
];

export default function HowItWorksSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });

  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: stagger },
    },
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 8 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: duration * 0.6, ease: appleEase } 
    },
  };

  const slideUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration, ease: appleEase } 
    },
  };

  const lineReveal = {
    hidden: { scaleX: 0, originX: 0 },
    visible: { 
      scaleX: 1, 
      transition: { duration: duration * 0.7, ease: appleEase } 
    },
  };

  return (
    <section ref={ref} className="py-24 md:py-32 lg:py-40 bg-muted/30">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="max-w-2xl mx-auto text-center mb-16 md:mb-20 lg:mb-24"
        >
          <motion.p
            variants={fadeIn}
            className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-primary mb-4"
          >
            How it works
          </motion.p>

          <motion.h2
            variants={slideUp}
            className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] mb-4"
          >
            Learning made simple
          </motion.h2>

          <motion.div
            variants={lineReveal}
            className="h-[2px] w-16 bg-gradient-to-r from-foreground/30 to-transparent mx-auto mb-6"
          />

          <motion.p
            variants={slideUp}
            className="text-base md:text-lg text-muted-foreground leading-relaxed"
          >
            Three steps to academic success. No complexity, no confusion.
          </motion.p>
        </motion.div>

        {/* Steps with premium card hover effects */}
        <div className="relative grid md:grid-cols-3 gap-8 md:gap-12 lg:gap-16">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 32 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 32 }}
              transition={{ duration, ease: appleEase, delay: 0.3 + index * 0.12 }}
              whileHover={{ y: -4, transition: { duration: 0.3, ease: appleEase } }}
              className="group relative z-10 text-center p-6 rounded-2xl transition-all duration-500 hover:bg-muted hover:shadow-xl hover:shadow-black/30"
            >
              {/* Icon with subtle scale on hover */}
              <motion.div 
                className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent/10 mb-6 transition-all duration-500 group-hover:bg-accent/20 group-hover:scale-105"
              >
                <step.icon className="w-7 h-7 text-accent transition-transform duration-500 group-hover:scale-110" />
              </motion.div>
              
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-3">
                {step.number}
              </p>
              <h3 className="font-display text-xl md:text-2xl font-semibold mb-3 transition-colors duration-300">
                {step.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
